import React from "react";
import {
    Ticket as TicketIcon,
    Calendar,
    MapPin,
    Printer,
    ChevronLeft,
    ChevronRight,
    User as UserIcon,
    Users,
    CheckCircle,
} from "lucide-react";

/* =========================================================================
 *  Shared ticket UI components for the My Tickets and Lookup pages.
 *
 *  Pulled out so both pages render the exact same card design + inline
 *  detail panel — the only difference between the two pages is where the
 *  list comes from (paginated /my-tickets vs. single /public/ticket-lookup
 *  response). The shared file keeps the visual contract in one place.
 * ========================================================================= */

/* ----------------------------- date helpers ----------------------------- */
export const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

export const formatTimeRange = (startTime, endTime) => {
    const toLabel = (t) => {
        if (!t) return null;
        const parts = t.split(":");
        const d = new Date();
        d.setHours(parseInt(parts[0], 10) || 0);
        d.setMinutes(parseInt(parts[1], 10) || 0);
        return d.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    };
    const start = toLabel(startTime);
    const end = toLabel(endTime);
    if (start && end) return `${start} - ${end}`;
    return start || "";
};

export const formatDateTime = (dateString, to) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";
    if (to === "time") {
        return date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    }
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

const APS_OUTLINE_LOGO =
    "https://apsession.s3.us-east-1.amazonaws.com/Apsession+outline.png";
export const QR_FALLBACK =
    "https://upload.wikimedia.org/wikipedia/commons/d/d0/QR_code_for_mobile_English_Wikipedia.svg";

/* ============================================================================
 *  printTickets — open a clean pop-up window with one printable ticket per
 *  attendee and trigger the browser print dialog.
 *
 *  Shared by My Tickets and the Lookup result page so both produce the
 *  identical printout (logo header, status bar, event/date/venue, QR + ids)
 *  instead of printing the live page with all its app chrome. Each caller
 *  passes already-formatted strings + a normalized `stubs` array so this
 *  template stays agnostic to the two pages' different data shapes.
 *
 *  We wait for the QR images to finish loading before calling print(),
 *  otherwise they come out blank.
 * ============================================================================ */
export function printTickets({
    eventName,
    dateStr,
    timeStr,
    venueFull,
    venueShort,
    isPast = false,
    stubs = [],
    total,
}) {
    if (!stubs.length) return;
    const count = total || stubs.length || 1;
    const win = window.open("", "_blank", "width=820,height=920");
    if (!win) {
        // Popup blocked — fall back to printing the current page.
        window.print();
        return;
    }
    const esc = (s) =>
        String(s ?? "").replace(/[&<>"]/g, (c) => (
            { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]
        ));
    const LOGO = "https://apsession.s3.us-east-1.amazonaws.com/Apsession+outline.png";
    const QR_FALLBACK =
        "https://upload.wikimedia.org/wikipedia/commons/d/d0/QR_code_for_mobile_English_Wikipedia.svg";
    const dStr = esc(dateStr);
    const tStr = esc(timeStr);
    const vFull = esc(venueFull);
    const vShort = esc(venueShort || (venueFull || "").split(",")[0] || "Venue");
    const cards = stubs
        .map((s, i) => {
            const cid = esc(s.confirmation_id || `${i + 1}`);
            const ticketType = esc(s.ticket_type || "General Admission");
            const holderName = esc(s.holder_name || "—");
            const qr = esc(s.barcode || QR_FALLBACK);
            return `
        <div class="ticket">
          <div class="header"><img class="logo" src="${esc(LOGO)}" /></div>
          <div class="statusbar">
            <span class="tnum">Ticket #${i + 1} of ${count}</span>
            <span class="badge">${isPast ? "Expired" : "Upcoming"}</span>
          </div>
          <div class="body">
            <h1 class="title">${esc(eventName)}</h1>
            <div class="cols">
              <div class="col">
                <p class="label">Date &amp; Time</p>
                <p class="val">${dStr}</p>
                <p class="sub">${tStr}</p>
              </div>
              <div class="col">
                <p class="label">Location</p>
                <p class="val">${vShort}</p>
                <p class="sub">${vFull}</p>
              </div>
            </div>
            <div class="qrrow">
              <div class="qrbox${isPast ? " expired" : ""}">
                <img class="qr" src="${qr}" />
                ${isPast ? '<div class="stamp">Expired</div>' : ""}
              </div>
              <!-- Same three fields, in the same order, as InlineTicketView's
                   detail column. Ticket Holder was missing here and Admits was
                   still printed after being dropped from the on-screen view. -->
              <div class="details">
                <div><p class="dlabel">Confirmation ID</p><p class="pill">${cid}</p></div>
                <div><p class="dlabel">Ticket Holder</p><p class="dval">${holderName}</p></div>
                <div><p class="dlabel">Ticket Type</p><p class="dval">${ticketType}</p></div>
              </div>
            </div>
          </div>
        </div>`;
        })
        .join("");
    win.document.write(`<!doctype html><html><head><title>Tickets — ${esc(eventName)}</title>
      <style>
        *{box-sizing:border-box}
        html,body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
        body{font-family:Arial,Helvetica,sans-serif;margin:0;padding:24px;color:#111;background:#f9fafb}
        .ticket{background:#fff;border:1px solid #e5e7eb;border-radius:32px;overflow:hidden;max-width:560px;margin:0 auto 28px;box-shadow:0 10px 30px rgba(0,0,0,.08);page-break-inside:avoid}
        .header{background:#ffea00;height:96px;display:flex;align-items:center;justify-content:center}
        .header .logo{height:46px;width:auto;object-fit:contain}
        .statusbar{background:#000;color:#fff;display:flex;justify-content:space-between;align-items:center;padding:10px 28px}
        .statusbar .tnum{font-weight:bold;font-size:13px}
        .statusbar .badge{font-weight:800;font-size:11px;text-transform:uppercase;background:rgba(255,255,255,.2);padding:3px 8px;border-radius:4px}
        .body{padding:32px}
        .title{font-size:26px;font-weight:800;text-align:center;margin:0 0 28px;letter-spacing:-.02em}
        .cols{display:flex;gap:24px;border-bottom:1px solid #f0f0f0;padding-bottom:24px;margin-bottom:24px}
        .col{flex:1}
        .label{color:#6b7280;font-weight:bold;font-size:12px;text-transform:uppercase;letter-spacing:.05em;margin:0 0 6px}
        .val{font-weight:800;color:#111;margin:0 0 4px;font-size:15px}
        .sub{color:#374151;font-weight:bold;margin:0;font-size:13px;line-height:1.35}
        /* nowrap + a flexible details column: ticket-type names can be long
           ("Payment fee -> buyer, Service fee -> host"), and with a rigid
           min-width the row overflowed the 560px card and wrapped the details
           underneath the QR instead of sitting beside it. */
        .qrrow{display:flex;align-items:center;justify-content:center;gap:24px;flex-wrap:nowrap}
        .qrbox{width:200px;height:200px;background:#fff;border:1px solid #e5e7eb;border-radius:16px;padding:12px;display:flex;align-items:center;justify-content:center;position:relative;flex:0 0 200px}
        .qr{width:100%;height:100%;object-fit:contain;mix-blend-mode:multiply}
        .qrbox.expired .qr{opacity:.15;filter:grayscale(1) blur(4px)}
        .stamp{position:absolute;inset:0;display:flex;align-items:center;justify-content:center}
        .stamp:after{content:"Expired";border:4px solid #ef4444;color:#ef4444;font-weight:800;font-size:26px;letter-spacing:.1em;text-transform:uppercase;padding:6px 14px;transform:rotate(-15deg);background:rgba(255,255,255,.7)}
        .details{flex:1 1 auto;min-width:0}
        .details>div{margin-bottom:16px}
        .details>div:last-child{margin-bottom:0}
        .dlabel{color:#9ca3af;font-weight:bold;font-size:10px;text-transform:uppercase;letter-spacing:.1em;margin:0 0 4px}
        .pill{font-weight:800;font-size:17px;color:#111;background:#f3f4f6;padding:4px 10px;border-radius:8px;display:inline-block;margin:0;overflow-wrap:anywhere}
        .dval{font-weight:800;font-size:16px;line-height:1.3;color:#111;margin:0;overflow-wrap:anywhere}
        @media print{body{padding:0;background:#fff}.ticket{box-shadow:none;border:1px solid #e5e7eb}}
      </style></head><body>${cards}</body></html>`);
    win.document.close();
    win.focus();
    const imgs = win.document.images;
    let pending = imgs.length;
    const triggerPrint = () => {
        win.print();
    };
    if (!pending) {
        triggerPrint();
    } else {
        for (let i = 0; i < imgs.length; i++) {
            const im = imgs[i];
            if (im.complete) {
                if (--pending === 0) triggerPrint();
            } else {
                im.onload = im.onerror = () => {
                    if (--pending === 0) triggerPrint();
                };
            }
        }
    }
}

/* ============================================================================
 *  OrderCard — the left-pane list card.
 *
 *  Cover image with gradient overlay, event title baked into the bottom
 *  of the image, yellow ticket-count chip top-right, then a small bottom
 *  panel with date + venue and (on mobile only) a black "View Tickets"
 *  CTA that opens the stub modal.
 * ============================================================================ */
export function OrderCard({ order, isSelected, onSelect, onOpenViewer }) {
    const ev = order.event || {};
    return (
        <div
            onClick={() => onSelect?.(order)}
            className={`bg-white rounded-2xl overflow-hidden cursor-pointer transition-all border-2 flex flex-col ${
                isSelected
                    ? "border-[#ffea00] shadow-lg ring-4 ring-[#ffea00]/10 lg:scale-[1.02]"
                    : "border-gray-100 hover:border-gray-300 shadow-sm"
            }`}
        >
            <div className="relative aspect-[2/1] w-full overflow-hidden bg-gray-200">
                {ev.image && (
                    <img
                        src={ev.image}
                        alt={ev.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src =
                                "/assets/images/event-cover-paceholder.png";
                        }}
                    />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-3 left-4 right-4">
                    <h4 className="text-white font-black text-lg truncate drop-shadow-md">
                        {ev.title}
                    </h4>
                </div>
                <div className="hidden lg:flex absolute top-3 right-3 bg-[#ffea00] text-black text-xs font-black px-2.5 py-1.5 rounded-lg shadow-md items-center">
                    <TicketIcon size={14} className="mr-1.5" />{" "}
                    {order.qty} Ticket{order.qty !== 1 ? "s" : ""}
                </div>
            </div>

            <div className="p-4 space-y-2 bg-white flex flex-col">
                <div className="flex items-center text-sm font-bold text-gray-500">
                    <Calendar size={16} className="mr-2 text-[#ffea00] flex-shrink-0" />
                    <span className="truncate">{formatDate(ev.date)}</span>
                </div>
                <div className="flex items-center text-sm font-bold text-gray-500">
                    <MapPin size={16} className="mr-2 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{ev.venue || ev.address}</span>
                </div>
                {onOpenViewer && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onOpenViewer(order);
                        }}
                        className="lg:hidden mt-3 w-full bg-black hover:bg-gray-800 text-[#ffea00] font-black py-3 rounded-xl shadow-md transition-all flex items-center justify-center text-sm"
                    >
                        <TicketIcon size={16} className="mr-2" /> View Tickets (
                        {order.qty})
                    </button>
                )}
            </div>
        </div>
    );
}

/* ============================================================================
 *  InlineTicketView — the desktop right-pane detailed view.
 *
 *  Shared by My Tickets and Ticket Lookup so both pages render the same
 *  expanded ticket design.
 * ============================================================================ */
export function InlineTicketView({
    order,
    ticket,
    stubIndex,
    setStubIndex,
    isExpired: isExpiredProp,
    statusLabel,
    onPrint,
    onPreviousStub,
    onNextStub,
    canGoPrevious,
    canGoNext,
}) {
    const stubs = ticket?.stubs || order?.stubs || [];
    const stub = stubs[stubIndex] || stubs[0] || {};
    const ev = ticket?.event || order?.event || {};
    const qty = ticket?.qty || order?.qty || stubs.length || 1;
    const isExpired =
        isExpiredProp ??
        (order?.status === "past" || order?.status === "expired");

    const title = ticket?.eventname || ev.title || ev.name || "";
    const activationDate =
        ticket?.activation_date || stub.activation_date || ev.date || null;
    const expirationDate =
        ticket?.expiration_date || stub.expiration_date || ev.end_date || null;
    const venueFull = ev.venueaddress || ev.address || "";
    const venueShort =
        (ev.venueaddress || ev.venue || ev.address || "").split(",")[0] ||
        "Venue";
    const ticketType =
        stub.ticket_type ||
        ticket?.ticket_type ||
        order?.type ||
        "General Admission";
    // Attendee name is optional and stored NULL when skipped — "Guest" is the
    // display fallback everywhere a ticket holder is shown.
    const holderName =
        [stub.firstname, stub.lastname].filter(Boolean).join(" ").trim() ||
        "Guest";
    const confirmationId =
        stub.confirmation_id ||
        `${ticket?.order_id || order?.confirmation_id || `APS-${ticket?.id || "ticket"}`}-${stubIndex + 1}`;
    const status =
        statusLabel ||
        (order?.status === "past" || order?.status === "expired"
            ? "Expired"
            : order?.status
              ? order.status.charAt(0).toUpperCase() + order.status.slice(1)
              : "Upcoming");

    const handlePrint =
        onPrint ||
        (() => {
            printTickets({
                eventName: title,
                dateStr: activationDate
                    ? formatDateTime(activationDate, "date")
                    : formatDate(ev.date),
                timeStr:
                    activationDate && expirationDate
                        ? `${formatDateTime(activationDate, "time")} - ${formatDateTime(expirationDate, "time")}`
                        : formatTimeRange(ev.time, ev.end_time),
                venueFull,
                venueShort,
                isPast: isExpired,
                total: qty,
                // Each field derived exactly as the on-screen view derives it,
                // including the confirmation-id fallback — printing used to fall
                // back to a bare row number ("1") where the view showed
                // "<order>-1".
                stubs: (stubs.length ? stubs : [{}]).map((s, i) => ({
                    confirmation_id:
                        s.confirmation_id ||
                        `${ticket?.order_id || order?.confirmation_id || `APS-${ticket?.id || "ticket"}`}-${i + 1}`,
                    ticket_type: s.ticket_type || ticketType,
                    holder_name:
                        [s.firstname, s.lastname].filter(Boolean).join(" ").trim() || "Guest",
                    barcode: s.barcode,
                })),
            });
        });

    const dateLabel = activationDate
        ? formatDateTime(activationDate, "date")
        : formatDate(ev.date);
    const timeLabel =
        activationDate && expirationDate
            ? `${formatDateTime(activationDate, "time")} - ${formatDateTime(expirationDate, "time")}`
            : formatTimeRange(ev.time, ev.end_time);

    return (
        <div className="flex flex-col items-end">
            <button
                type="button"
                onClick={handlePrint}
                className="mb-4 bg-[#14A5C9] hover:bg-[#108ba8] border-none shadow-xl text-white px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider flex items-center shadow-md transition-colors"
            >
                <Printer size={14} className="mr-2" /> Print Page
            </button>

            <div className="w-full bg-white rounded-[2rem] shadow-2xl border border-gray-200 overflow-hidden relative">
                <div className="bg-[#ffea00] h-28 flex items-center justify-center relative">
                    <img
                        src={APS_OUTLINE_LOGO}
                        alt="Apsession"
                        className="h-12 w-auto object-contain drop-shadow-md"
                    />
                    <div className="absolute -bottom-4 -left-4 w-8 h-8 bg-gray-50 rounded-full border-t border-r border-gray-200 z-10" />
                    <div className="absolute -bottom-4 -right-4 w-8 h-8 bg-gray-50 rounded-full border-t border-l border-gray-200 z-10" />
                </div>

                <div className="bg-black text-white px-8 py-3 flex justify-between items-center relative z-0">
                    <div className="flex items-center">
                        <span className="font-bold text-sm mr-4">
                            Ticket #{stubIndex + 1} of {qty}
                        </span>
                        {qty > 1 && (setStubIndex || onPreviousStub) && (
                            <div className="flex space-x-3">
                                <button
                                    type="button"
                                    onClick={
                                        onPreviousStub ||
                                        (() =>
                                            setStubIndex(
                                                Math.max(0, stubIndex - 1)
                                            ))
                                    }
                                    disabled={
                                        canGoPrevious !== undefined
                                            ? !canGoPrevious
                                            : stubIndex === 0
                                    }
                                    className="p-2 rounded-full bg-[#ffea00] border-none hover:bg-[#e6d300] disabled:bg-gray-800 disabled:text-gray-600 transition-all text-black flex items-center justify-center shadow-md"
                                >
                                    <ChevronLeft size={16} strokeWidth={3} />
                                </button>
                                <button
                                    type="button"
                                    onClick={
                                        onNextStub ||
                                        (() =>
                                            setStubIndex(
                                                Math.min(
                                                    qty - 1,
                                                    stubIndex + 1
                                                )
                                            ))
                                    }
                                    disabled={
                                        canGoNext !== undefined
                                            ? !canGoNext
                                            : stubIndex === qty - 1
                                    }
                                    className="p-2 rounded-full bg-[#ffea00] border-none hover:bg-[#e6d300] disabled:bg-gray-800 disabled:text-gray-600 transition-all text-black flex items-center justify-center shadow-md"
                                >
                                    <ChevronRight size={16} strokeWidth={3} />
                                </button>
                            </div>
                        )}
                    </div>
                    <span className="font-black text-xs bg-white/20 px-2 py-1 rounded uppercase">
                        {status}
                    </span>
                </div>

                <div className="p-8 md:p-12 relative">
                    <h2 className="text-3xl md:text-4xl font-black text-center text-gray-900 mb-10 tracking-tight leading-tight">
                        {title}
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10 text-center md:text-left border-b border-gray-100 pb-10">
                        <div>
                            <p className="text-gray-500 font-bold text-left text-sm uppercase tracking-wider mb-2">
                                Date & Time
                            </p>
                            <p className="font-black text-left mb-2 text-gray-900">
                                {dateLabel}
                            </p>
                            <p className="text-gray-700 font-bold text-left mt-1">
                                {timeLabel}
                            </p>
                        </div>
                        <div>
                            <p className="text-gray-500 text-left font-bold text-sm uppercase tracking-wider mb-2">
                                Location
                            </p>
                            <p className="font-black text-left text-gray-900">
                                {venueShort}
                            </p>
                            <p className="text-gray-700 text-left font-bold mt-1 leading-snug max-w-xs">
                                {venueFull}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12 mb-12">
                        <div className="flex flex-col items-center">
                            <div className="w-56 h-56 md:w-64 md:h-64 bg-white p-3 rounded-2xl shadow-sm border border-gray-200 flex items-center justify-center flex-shrink-0 relative overflow-hidden mb-4">
                                {stub.barcode ? (
                                    <img
                                        src={stub.barcode}
                                        alt="Ticket QR Code"
                                        className={`w-full h-full mix-blend-multiply pointer-events-none transition-all ${
                                            isExpired
                                                ? "opacity-15 blur-[5px] grayscale"
                                                : "opacity-90"
                                        }`}
                                    />
                                ) : (
                                    <div className="text-gray-400 font-bold text-sm text-center px-4">
                                        Generating QR…
                                    </div>
                                )}
                                {isExpired && (
                                    <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                                        <div className="border-[4px] border-red-500 text-red-500 font-black text-2xl md:text-3xl tracking-widest uppercase px-4 py-2 rotate-[-15deg] shadow-sm bg-white/70 backdrop-blur-sm">
                                            Expired
                                        </div>
                                    </div>
                                )}
                            </div>
                            {!isExpired && (
                                <div className="flex gap-2 w-full">
                                    <button
                                        type="button"
                                        className="flex-1 bg-black text-white rounded-lg border-none py-2 flex items-center justify-center font-bold text-xs transition-transform hover:scale-105 shadow-sm"
                                    >
                                        <svg
                                            className="w-3.5 h-3.5 mr-1.5"
                                            fill="currentColor"
                                            viewBox="0 0 24 24"
                                            aria-hidden
                                        >
                                            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.19 2.31-.88 3.5-.8 1.53.1 2.76.75 3.52 1.9-2.92 1.69-2.39 5.56.34 6.75-.68 1.63-1.63 3.31-2.44 4.32zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.3 2.31-2.02 4.39-3.74 4.25z" />
                                        </svg>
                                        Apple
                                    </button>
                                    <button
                                        type="button"
                                        className="flex-1 bg-white border border-gray-300 text-gray-800 rounded-lg py-2 flex items-center justify-center font-bold text-xs transition-transform hover:scale-105 shadow-sm"
                                    >
                                        <svg
                                            className="w-3.5 h-3.5 mr-1.5"
                                            viewBox="0 0 24 24"
                                            aria-hidden
                                        >
                                            <path
                                                fill="#4285F4"
                                                d="M21.35,11.1H12.18V13.83H18.69C18.36,17.64 15.19,19.27 12.19,19.27C8.36,19.27 5,16.25 5,12C5,7.9 8.2,4.73 12.2,4.73C15.29,4.73 17.1,6.7 17.1,6.7L19.04,4.78C19.04,4.78 16.53,2 12.2,2C6.48,2 2,6.5 2,12C2,17.5 6.48,22 12.2,22C17.92,22 21.84,18 21.84,12.2C21.84,11.83 21.8,11.45 21.8,11.45L21.35,11.1Z"
                                            />
                                        </svg>
                                        Google
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="space-y-5 text-center md:text-left">
                            <TicketDetailField
                                icon={<CheckCircle size={12} className="mr-1" />}
                                label="Confirmation ID"
                                value={
                                    <p className="font-black text-left w-100 text-xl text-gray-900 bg-gray-100 py-1 rounded-lg inline-block">
                                        {confirmationId}
                                    </p>
                                }
                            />
                            <TicketDetailField
                                icon={<UserIcon size={12} className="mr-1" />}
                                label="Ticket Holder"
                                value={
                                    <p className="font-black text-xl text-left text-gray-900">
                                        {holderName}
                                    </p>
                                }
                            />
                            <TicketDetailField
                                icon={<TicketIcon size={12} className="mr-1" />}
                                label="Ticket Type"
                                value={
                                    <p className="font-black text-xl text-left text-gray-900">
                                        {ticketType}
                                    </p>
                                }
                            />
                            {/* <TicketDetailField
                                icon={<Users size={12} className="mr-1" />}
                                label="Admits"
                                value={
                                    <p className="font-black text-xl text-left text-gray-900">
                                        {qty} Person(s)
                                    </p>
                                }
                            /> */}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const TicketDetailField = ({ icon, label, value }) => (
    <div>
        <p className="text-gray-400 font-bold text-[10px] uppercase tracking-widest mb-1 flex items-center justify-center md:justify-start">
            {icon} {label}
        </p>
        {value}
    </div>
);

/* ============================================================================
 *  MobileTicketStub — single mobile ticket stub in the carousel.
 *
 *  Shared by My Tickets and the Lookup purchased-ticket modal. Callers pass
 *  `renderQr` so each page keeps its own QR rendering logic untouched.
 * ============================================================================ */
export function MobileTicketStub({
    confirmationId,
    attendeeName,
    ticketType,
    dateTimeLabel,
    venueLabel,
    venueAddressLabel = "",
    isExpired = false,
    renderQr,
    showWalletButtons = true,
}) {
    return (
        <div className="w-[85vw] max-w-[320px] shrink-0 snap-center snap-always bg-white rounded-none shadow-2xl flex flex-col relative overflow-hidden mr-4 last:mr-0 h-auto min-h-[510px] pb-6">
            <div className="" />
            <div className="" />
            <div className="absolute left-4 right-4 top-[270px] border-t border-dashed border-gray-200 z-10" />

            <div className="flex flex-col h-full py-2 relative z-10">
                <div className="flex flex-col items-center pt-5 shrink-0">
                    <img
                        src={APS_OUTLINE_LOGO}
                        alt="Apsession"
                        className="h-7 mt-5 w-auto object-contain mb-3 drop-shadow-sm"
                    />
                    <div className="relative flex items-center justify-center w-52 h-52">
                        {renderQr?.()}
                        {isExpired && (
                            <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                                <div className="border-[4px] border-red-500 text-red-500 font-black text-3xl tracking-widest uppercase px-4 py-2 rotate-[-15deg] shadow-sm bg-white/70 backdrop-blur-sm">
                                    Expired
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-col mt-4">
                    <div className="text-center mb-4">
                        <p className="text-gray-400 font-bold text-[10px] uppercase tracking-widest mb-0">
                            Confirmation ID
                        </p>
                        <p className="font-black text-lg text-gray-900 leading-none mt-1">
                            {confirmationId}
                        </p>
                    </div>
                    <div className="bg-gray-50/90 mx-4 backdrop-blur-md rounded-2xl p-3 border border-gray-100 shadow-sm space-y-2.5">
                        <div className="flex items-center text-gray-800">
                            <UserIcon size={13} className="mr-2 text-gray-400 flex-shrink-0" />
                            <span className="font-black text-xs text-gray-900 capitalize line-clamp-1">
                                {attendeeName || "Guest"}
                            </span>
                        </div>
                        <div className="flex items-center text-gray-800">
                            <TicketIcon
                                size={13}
                                className="mr-2 text-[#e6d300] flex-shrink-0"
                            />
                            <span className="font-black text-xs text-gray-900 line-clamp-1">
                                {ticketType || "General Admission"}
                            </span>
                        </div>
                        {dateTimeLabel && (
                            <div className="flex items-center text-gray-600">
                                <Calendar
                                    size={13}
                                    className="mr-2 text-gray-400 flex-shrink-0"
                                />
                                <span className="font-bold text-xs line-clamp-1">
                                    {dateTimeLabel}
                                </span>
                            </div>
                        )}
                        {/* Location, below the date row: venue name first, then
                            the street address underneath — the same two-line
                            block the desktop inline view renders. The address
                            line is omitted when it would just repeat the venue
                            name. */}
                        {(venueLabel || venueAddressLabel) && (
                            <div className="flex items-start text-gray-600">
                                <MapPin
                                    size={13}
                                    className="mr-2 text-gray-400 flex-shrink-0 mt-0.5"
                                />
                                <span className="leading-tight">
                                    {venueLabel && (
                                        <span className="block font-black text-xs text-gray-900 line-clamp-1">
                                            {venueLabel}
                                        </span>
                                    )}
                                    {venueAddressLabel && (
                                        <span className="block font-bold text-xs line-clamp-2">
                                            {venueAddressLabel}
                                        </span>
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {showWalletButtons && !isExpired && (
                    <div className="hidden px-4 sm:flex flex-row gap-3 mt-6 w-full pt-2 border-t border-gray-100">
                        <button
                            type="button"
                            className="flex-1 bg-black text-white rounded-xl py-2.5 flex items-center justify-center font-bold text-[13px] transition-transform hover:scale-[1.02] shadow-sm border-none"
                        >
                            <svg
                                className="w-4 h-4 mr-2"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden
                            >
                                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.19 2.31-.88 3.5-.8 1.53.1 2.76.75 3.52 1.9-2.92 1.69-2.39 5.56.34 6.75-.68 1.63-1.63 3.31-2.44 4.32zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.3 2.31-2.02 4.39-3.74 4.25z" />
                            </svg>
                            Apple Wallet
                        </button>
                        <button
                            type="button"
                            className="flex-1 bg-white border border-gray-300 text-gray-800 rounded-xl py-2.5 flex items-center justify-center font-bold text-[13px] transition-transform hover:scale-[1.02] shadow-sm hover:bg-gray-50"
                        >
                            <svg
                                className="w-4 h-4 mr-2"
                                viewBox="0 0 24 24"
                                aria-hidden
                            >
                                <path
                                    fill="#4285F4"
                                    d="M21.35,11.1H12.18V13.83H18.69C18.36,17.64 15.19,19.27 12.19,19.27C8.36,19.27 5,16.25 5,12C5,7.9 8.2,4.73 12.2,4.73C15.29,4.73 17.1,6.7 17.1,6.7L19.04,4.78C19.04,4.78 16.53,2 12.2,2C6.48,2 2,6.5 2,12C2,17.5 6.48,22 12.2,22C17.92,22 21.84,18 21.84,12.2C21.84,11.83 21.8,11.45 21.8,11.45L21.35,11.1Z"
                                />
                            </svg>
                            Google Wallet
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
